/**
 * The Open Items Register, as the record holds it (ADR-0073 step 3 — "open items get their own surface").
 *
 * Step 2 kept the register out of the facts panel on purpose (~150 items would have been nine rows in
 * ten there) and left one line of counts. This is the surface that line pointed at: every current item,
 * the late ones first, closures one click away.
 *
 * ## It also carries the register's WORKLISTS, which nothing read before
 *
 * An item "not yet datable" is undatable for one of two reasons, and they are different jobs: its event
 * matches NO milestone on the record's calendar (add one), or it matches a milestone that has NO DATE
 * yet (date it). Both are listed, separately — "add milestones" alone would send a reader to create a
 * milestone that already exists (red-team, 2026-09-29).
 *
 * ## What it deliberately does NOT do
 *
 * It edits nothing. The spreadsheet stays the source of truth until the model is proven (D3). And it does
 * not claim to know which register version was imported last — the record does not keep that (an
 * unchanged re-import leaves no trace); it names the newest version any item was last CHANGED in.
 */

import { useEffect, useState } from 'react';
import {
  getCrosswalk,
  getOpenItems,
  getScheduleCrosswalk,
  type CrosswalkView,
  type OpenItemsView,
  type RecordFactView,
  type ScheduleCrosswalkView,
} from '../api.js';
import { usePrintExpanded } from './usePrintExpanded.js';

/** The fields of an `open_item` value this panel reads. Everything else stays on the record. */
interface OpenItemValue {
  status?: string;
  question?: string | null;
  gate?: string | null;
  decided?: string | null;
  decidedBy?: string | null;
  decidedOn?: string | null;
}

const valueOf = (f: RecordFactView): OpenItemValue =>
  f.value && typeof f.value === 'object' ? (f.value as OpenItemValue) : {};

/** The item's number, for ordering — "OI-111" after "OI-12". */
const itemNo = (key: string) => Number(/\d+/.exec(key)?.[0] ?? Number.MAX_SAFE_INTEGER);

/** Overdue first, then owed, then what cannot be dated yet — the order a reader acts in. */
const STATE_ORDER: Readonly<Record<string, number>> = { overdue: 0, owed: 1, unresolved: 2 };

const dueTime = (f: RecordFactView) =>
  f.resolution?.dueDate ? new Date(f.resolution.dueDate).getTime() : Number.MAX_SAFE_INTEGER;

/**
 * A due DATE, never a time. A register date has no time and no zone; it is stored as the end of that
 * day in UTC, so a local-time formatter printed "7:59 PM" — and, east of UTC, the next day's date.
 */
export function dayOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Dotted versions compared as numbers — v3.9 < v3.23. */
function newestVersion(items: readonly RecordFactView[]): string | null {
  const nums = items
    .map((f) => /\bv(\d+(?:\.\d+)*)\b/.exec(f.arrivalPath)?.[1])
    .filter((v): v is string => v !== undefined)
    .map((v) => v.split('.').map(Number));
  nums.sort((a, b) => {
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
      const d = (a[i] ?? 0) - (b[i] ?? 0);
      if (d !== 0) return d;
    }
    return 0;
  });
  const newest = nums.at(-1);
  return newest ? `v${newest.join('.')}` : null;
}

function StateLabel({ fact }: { fact: RecordFactView }) {
  const r = fact.resolution;
  if (!r) return <span className="muted">—</span>;
  if (r.state === 'overdue') {
    return (
      <span className="oi__state oi__state--overdue">
        overdue{r.dueDate ? ` · was due ${dayOnly(r.dueDate)}` : ''}
      </span>
    );
  }
  if (r.state === 'owed') {
    return <span className="oi__state">due {r.dueDate ? dayOnly(r.dueDate) : ''}</span>;
  }
  // ⚠️ NOT "fine": an item nobody can date is one the record cannot answer for yet.
  return <span className="oi__state oi__state--unresolved">not yet datable</span>;
}

/** A count on screen, the names one click away — the record's worklist shape. */
function Worklist({ label, items }: { label: string; items: readonly string[] }) {
  const ref = usePrintExpanded();
  if (items.length === 0) return null;
  return (
    <details className="worklist" ref={ref}>
      <summary>{label}</summary>
      <ul className="worklist__items">
        {items.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </details>
  );
}

/** The pure panel — every state assertable under SSR (see ProjectDrawings for why it is split). */
export function OpenItemsPanel({ view }: { view: OpenItemsView | null }) {
  const closedRef = usePrintExpanded();

  if (!view) return <p className="card__note">Loading the Open Items Register…</p>;
  if (!view.loaded) {
    // Said as a failure, never as an empty register — "no open questions" would be a false claim.
    return <p className="card__note">The Open Items Register could not be loaded. Try again.</p>;
  }
  if (view.items.length === 0) {
    return (
      <p className="card__note">
        No Open Items Register is on this record yet. It arrives through{' '}
        <code>pnpm open-items:import</code>; the spreadsheet stays the source of truth until then.
      </p>
    );
  }

  const status = (f: RecordFactView) => valueOf(f).status;
  const live = view.items
    .filter((f) => status(f) === 'open' || status(f) === 'answered')
    .sort(
      (a, b) =>
        (STATE_ORDER[a.resolution?.state ?? ''] ?? 3) -
          (STATE_ORDER[b.resolution?.state ?? ''] ?? 3) ||
        dueTime(a) - dueTime(b) ||
        itemNo(a.key) - itemNo(b.key),
    );
  const closed = view.items
    .filter((f) => status(f) === 'closed')
    .sort((a, b) => itemNo(b.key) - itemNo(a.key));
  const r = view.resolution;
  // ⚠️ No summary is "not known", never zero — "0 overdue" from a missing answer is a false claim.
  const n = (x: number | undefined) => (r ? String(x ?? 0) : '—');
  const version = newestVersion(view.items);
  const undatedMilestones = [
    ...new Set(
      live.flatMap((f) =>
        (f.resolution?.reasons ?? [])
          .filter((x) => x.code === 'milestone-undated')
          // A client's read carries no subject (canon "Where files go"); there is nothing to list.
          .flatMap((x) => (x.subject ? [x.subject] : [])),
      ),
    ),
  ].sort();
  const unmatched = r?.unmatchedEvents ?? [];

  return (
    <>
      <p className="card__note">
        <strong>{live.length}</strong> open · <strong>{n(r?.overdue)}</strong> overdue ·{' '}
        <strong>{n(r?.owed)}</strong> owed · <strong>{n(r?.unresolved)}</strong> not yet datable ·{' '}
        {closed.length} closed
        {version ? <span className="muted"> — latest change from register {version}</span> : null}
      </p>

      <Worklist
        label={`Add ${unmatched.length} milestone${unmatched.length === 1 ? '' : 's'} to the calendar so these items can be dated`}
        items={unmatched}
      />
      <Worklist
        label={`Date ${undatedMilestones.length} milestone${undatedMilestones.length === 1 ? '' : 's'} already on the calendar`}
        items={undatedMilestones}
      />

      <table className="oi__table" aria-label="Open items, the late ones first">
        <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">What has to be settled</th>
            <th scope="col">Owner</th>
            <th scope="col">Gate</th>
            <th scope="col">Where it stands</th>
          </tr>
        </thead>
        <tbody>
          {live.map((f) => {
            const v = valueOf(f);
            return (
              <tr key={f.id}>
                <th scope="row">{f.key}</th>
                <td>
                  {v.question}
                  {v.status === 'answered' && (
                    <>
                      {' '}
                      <span className="pill">answered</span>
                    </>
                  )}
                </td>
                <td>{f.ownerName}</td>
                <td>{v.gate}</td>
                <td>
                  <StateLabel fact={f} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {closed.length > 0 && (
        <details className="worklist" ref={closedRef}>
          <summary>{closed.length} closed and decided. Open to read them.</summary>
          <table className="oi__table" aria-label="Closed and decided, newest item first">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">The question</th>
                <th scope="col">What was decided</th>
                <th scope="col">By</th>
                <th scope="col">Date</th>
              </tr>
            </thead>
            <tbody>
              {closed.map((f) => {
                const v = valueOf(f);
                return (
                  <tr key={f.id}>
                    <th scope="row">{f.key}</th>
                    <td>{v.question}</td>
                    <td>{v.decided}</td>
                    <td>{v.decidedBy}</td>
                    <td>{v.decidedOn}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

/**
 * The design-review crosswalk (ADR-0073 D5), as a CHECKLIST.
 *
 * ⚠️ IT DOES NOT PROPOSE REOPENS, AND IT CALLS ITS GAPS THINGS TO CHECK. Measured on the live Commons
 * data, the first build's 8 proposals were 6 wrong — every reopen among them. So the panel lists pricing
 * findings no register item names (some will be covered by an item on the same subject), and the closed
 * items that name a still-open finding, for the person who runs the sweep to read. Every finding on the
 * record is accounted for, including those with no status this rule reads.
 */
export function CrosswalkPanel({ view }: { view: CrosswalkView | null }) {
  if (!view) return null;
  if (!view.loaded) {
    return <p className="card__note">The design-review crosswalk could not be loaded.</p>;
  }
  const c = view.counts;
  if (c.findings === 0 || view.items === 0) {
    return (
      <p className="card__note">
        Design-review crosswalk: nothing to compare yet —{' '}
        {c.findings === 0
          ? 'no design-review findings are on the record'
          : 'no register is on the record'}
        .
      </p>
    );
  }
  return (
    <>
      <h4 className="oi__subhead">Design-review crosswalk — things to check</h4>
      <p className="card__note">
        {c.findings} design-review {c.findings === 1 ? 'finding' : 'findings'} on the record:{' '}
        {c.paired} named by an open item · <strong>{c.gaps}</strong> open pricing{' '}
        {c.gaps === 1 ? 'finding' : 'findings'} no item names · {c.closedOnly} named only by closed
        items · {c.housekeeping} housekeeping (sweep report, not the register) · {c.notOpen}{' '}
        answered or closed
        {c.noStatus > 0 ? ` · ${c.noStatus} with no status this rule reads` : ''}. A machine matches
        IDs, not subjects — an item can cover a finding without naming it — so these are for the
        sweep to check, and any change is made in the register.
      </p>
      {view.gaps.length > 0 && (
        <ul className="oi__proposals">
          {view.gaps.map((g) => (
            <li key={g.findingKey}>
              <strong>{g.findingKey}</strong> ({g.status.toLowerCase()},{' '}
              {g.triage === 'BLOCKS_PRICING' ? 'blocks pricing' : 'moves pricing'}) — no register
              item names it. {g.findingSummary && <span className="muted">{g.findingSummary}</span>}
            </li>
          ))}
        </ul>
      )}
      {view.closedCitations.length > 0 && (
        <p className="card__note muted">
          Named only by closed items (re-read the closure if the finding has moved):{' '}
          {view.closedCitations.map((x) => `${x.findingKey} — ${x.itemKeys.join(', ')}`).join('; ')}
          .
        </p>
      )}
    </>
  );
}

/**
 * The schedule crosswalk (ADR-0069 N18), as a CHECKLIST. Canon: where the register and the schedule
 * disagree "the item says so and names both dates, and a person rules which one moves". Nothing here
 * changes the register; it names which schedule it read, so a stale reading is visible as stale.
 */
export function ScheduleCrosswalkPanel({ view }: { view: ScheduleCrosswalkView | null }) {
  if (!view) return null;
  if (!view.loaded) {
    return <p className="card__note">The schedule crosswalk could not be loaded.</p>;
  }
  const c = view.counts;
  if (view.items === 0 || c.linked === 0) {
    return (
      <p className="card__note">
        Schedule crosswalk: nothing to compare yet —{' '}
        {view.items === 0 ? 'no register is on the record' : 'no open item names a schedule task'}.
      </p>
    );
  }
  if (!view.schedule) {
    return (
      <p className="card__note">
        Schedule crosswalk: {c.linked} open {c.linked === 1 ? 'item names' : 'items name'} a
        schedule task, but the record holds no reading of the master schedule to compare them with.
        Not checked.
      </p>
    );
  }
  return (
    <>
      <h4 className="oi__subhead">Schedule crosswalk — things to check</h4>
      <p className="card__note">
        Compared with <strong>{view.schedule.filename}</strong>, as read{' '}
        {new Date(view.schedule.readAt).toLocaleDateString()}, which holds {view.schedule.covers} of
        the {view.schedule.of} linked tasks: {c.agree} agree · <strong>{c.disagree}</strong>{' '}
        {c.disagree === 1 ? 'finish differs' : 'finishes differ'}
        {c.notInSchedule > 0 ? ` · ${c.notInSchedule} task not found in that schedule` : ''}
        {c.ambiguous > 0 ? ` · ${c.ambiguous} named twice there, not compared` : ''}
        {c.unreadable > 0 ? ` · ${c.unreadable} not in the register's task form` : ''}. A person
        rules which date moves; any change is made in the register or the master.
      </p>
      {view.disagreements.length > 0 && (
        <ul className="oi__proposals">
          {view.disagreements.map((d) => (
            <li key={`${d.itemKey}-${d.taskNumber}`}>
              <strong>{d.itemKey}</strong> — task {d.taskNumber} {d.taskName}: the register carries
              finish {d.registerFinish}; the schedule reads {d.scheduleFinish}.
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** The fetching wrapper. `getOpenItems` never rejects — a failure arrives as `loaded: false`. */
export function OpenItemsRegister({ code }: { code: string }) {
  const [view, setView] = useState<OpenItemsView | null>(null);
  const [crosswalk, setCrosswalk] = useState<CrosswalkView | null>(null);
  const [schedule, setSchedule] = useState<ScheduleCrosswalkView | null>(null);
  useEffect(() => {
    let live = true;
    void getOpenItems(code).then((v) => {
      if (live) setView(v);
    });
    void getCrosswalk(code).then((c) => {
      if (live) setCrosswalk(c);
    });
    void getScheduleCrosswalk(code).then((c) => {
      if (live) setSchedule(c);
    });
    return () => {
      live = false;
    };
  }, [code]);
  return (
    <section className="card">
      <h3 className="card__title">Open Items Register</h3>
      <OpenItemsPanel view={view} />
      <CrosswalkPanel view={crosswalk} />
      <ScheduleCrosswalkPanel view={schedule} />
    </section>
  );
}
